import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link, Navigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Send, FileText, Users2, ArrowLeft, ChevronDown, Globe, LogOut, Building2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const NAV = [
  { to: '/business-portal', end: true, icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/business-portal/mass-payments', icon: Send, label: 'Mass payments' },
  { to: '/business-portal/templates', icon: Users2, label: 'Templates' },
  { to: '/business-portal/invoices', icon: FileText, label: 'Invoices' },
];

export function BusinessLayout() {
  const { user, logout, loading } = useAuth();
  const { t, lang, langs, setLang } = useLanguage();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get('/b2b/company/me').then(r => setCompany(r.data.company)).finally(() => setChecking(false));
  }, [user]);

  if (loading || checking) return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/business-portal" replace />;
  if (!company) return <Navigate to="/business-portal/register" replace />;

  const doLogout = () => { logout(); nav('/'); };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <header className="sticky top-0 z-40 border-b bg-white">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/business-portal" className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-[hsl(var(--brand-navy))] text-white flex items-center justify-center font-serif text-lg">C</div>
            <div className="leading-tight">
              <div className="font-serif text-base">{company.legal_name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Business portal · {company.verification_status}</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm"><Link to="/wallet"><ArrowLeft className="h-4 w-4 mr-1" />Personal</Link></Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="gap-1"><Globe className="h-4 w-4" />{langs.find(l => l.code === lang)?.label}<ChevronDown className="h-3 w-3" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">{langs.map(l => <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)}>{l.label} — {l.full}</DropdownMenuItem>)}</DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="gap-2"><Building2 className="h-4 w-4" />{user?.first_name}<ChevronDown className="h-3 w-3" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout}><LogOut className="h-4 w-4 mr-2" />{t('header.logout')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-[1400px] px-3 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="lg:sticky lg:top-24 h-fit">
          <nav className="rounded-2xl border bg-white p-2">
            {NAV.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={`b2b-nav-${item.to.split('/').pop() || 'dashboard'}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm ${isActive ? 'bg-[hsl(var(--brand-navy))] text-white' : 'hover:bg-[hsl(var(--secondary))]'}`}>
                <item.icon className="h-4 w-4" />{item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main><Outlet context={{ company, reloadCompany: () => api.get('/b2b/company/me').then(r => setCompany(r.data.company)) }} /></main>
      </div>
    </div>
  );
}
